import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, RotateCcw, ChevronRight, X } from 'lucide-react';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import { InfoTip } from '../components/shared/FormKit';
import { fetchJSON } from '../utils/api';
import { sectionTitle } from '../components/workflow/constants';

// Workflow notification mail rules (/settings/notifications).
// One matrix: per flow, per event — send or not, and which recipient groups.
// Edits apply immediately (the senders read the table per mail); "Default"
// badge = still on the built-in behaviour, no stored row.
//
// Sections start COLLAPSED — the matrix is long and most
// visits touch one flow, so each header carries a summary (events on / how
// many were customised) that answers "is anything odd in here?" without
// opening it.

const FLOW_LABELS = {
  client: 'New Client',
  qc: 'QC6 / QC7 Forms',
  eng: 'Engagement Setup',
  engreg: 'Engagement Registration',
  fs: 'Signed Financial Statements',
  qc35: 'QC35 Form',
  ear_update: 'Engagement Amendment — Update',
  ear_access: 'Engagement Amendment — CW/CC Access',
  cwcc: 'CaseWare, Charge Code & Team Update',
  esc: 'Engagement Status Change',
  screening: 'Screening',
  qca: 'QC Amendment',
  qcad: 'QC6 / QC7 Addendum',
  car: 'Client Amendment',
  fsa: 'Signed Financial Statements Amendment',
  qc35a: 'QC35 Form Amendment',
  cpd: 'External CPD',
  pq: 'Professional Qualification',
  absence: 'Training Absence',
  dispute: 'Attendance Dispute',
};

// Shown next to the flow heading where the flow is not self-explanatory.
// The L&D flows each answer to their own review permission, so "Reviewers"
// means someone different on each row — say which.
const FLOW_HINTS = {
  qc: 'Submitted / Re-submitted also governs the hand-off to the next reviewer',
  qca: 'Submitted / Re-submitted also governs the hand-off to the next reviewer',
  qcad: 'Submitted / Re-submitted also governs the hand-off to the next reviewer',
  screening: 'QM completes rather than approves; a registration-opened request pages nobody on submission',
  cwcc: 'Reviewers = holders of workflow:cwcc:review; QM = holders of workflow:cwcc:complete, paged on approval; each QM completion pages the case team',
  cpd: 'Files on submission — no submitted / approved mail; Reviewers = holders of ld:cpd:review',
  pq: 'Reviewers = holders of ld:pq:review',
  absence: 'Reviewers = holders of ld:absence:review',
  dispute: 'Reviewers = holders of ld:absence:review (shared with Training Absence)',
};

// Same-type flows share one section, each with its own reset.
const SECTIONS = [
  { key: 'client', title: 'Client', flows: ['client', 'car'] },
  { key: 'qc', title: 'QC6 / QC7', flows: ['qc', 'qcad', 'qca'] },
  { key: 'engagement', title: 'Engagement', flows: ['engreg', 'cwcc', 'esc', 'ear_update'] },
  { key: 'screening', title: 'Screening', flows: ['screening'] },
  { key: 'fs', title: 'Signed Financial Statements', flows: ['fs', 'fsa'] },
  { key: 'qc35', title: 'QC35 Form', flows: ['qc35', 'qc35a'] },
  // The L&D self-service flows are on this table too — their recipients are
  // configured here and the master switch mutes them like every other flow.
  { key: 'pd', title: 'Professional Development', flows: ['cpd', 'pq', 'absence', 'dispute'] },
];

const EVENT_LABELS = {
  // On the multi-stage flows this row also governs the hand-off mail (stage
  // n approved, now stage n+1).
  submitted: 'Submitted / Re-submitted',
  approved: 'Approved',
  approved_cw: 'Approved — CaseWare side',
  approved_cc: 'Approved — charge code side',
  completed_cw: 'Completed by QM — CaseWare',
  completed_cc: 'Completed by QM — charge code',
  completed_member: 'Completed by QM — team update',
  // Screening: QM attached the screening document.
  completed: 'Completed by QM',
  rejected: 'Returned for changes',
};

const GROUP_LABELS = {
  reviewers: 'Reviewer',
  qm: 'QM',
  requester: 'Requester',
  partner: 'Partner',
  director: 'Director',
  manager: 'Manager',
  eqr: 'EQR',
  team_members: 'Team Member(s)',
};

const GROUP_HINTS = {
  reviewers: "Whoever the event is about: on submission the reviewer named on the request, on a hand-off the reviewer whose turn just came, on approval / return every reviewer named on the request (keyed flows: everyone holding the flow's review permission)",
  qm: 'Holders of the CaseWare, Charge Code & Team Update completion permission',
  requester: 'The person who submitted the request',
  partner: 'The Partner in charge (request / engagement)',
  director: 'The Director (request / engagement)',
  manager: 'The Manager in charge (request / engagement)',
  eqr: 'The EQR (skipped for TO BE DETERMINED / NOT REQUIRED)',
  team_members: 'The team member list (request / engagement)',
};

// Any common separator ends an address: comma, semicolon (half- or full-width),
// the ideographic comma, spaces or line breaks — so a pasted Outlook list works.
// While typing, spaces do not split: they belong to a name being searched.
const EMAIL_SPLIT_RE = /[\s,;，；、]+/;
const TYPED_SPLIT_RE = /[,;，；、\n]+/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Extra addresses as removable chips. Typing searches the serving staff
// (name, email or designation) and a pick adds that person's email; a typed
// address outside the list is added on a separator, Enter or leaving the box.
// Anything that is not an email stays in the box, marked red, unsaved.
const ExtraEmails = ({ emails, disabled, onChange, staff }) => {
  const [input, setInput] = useState('');
  const [invalid, setInvalid] = useState(false);
  // An always-visible (and usually empty) address box doubled every row's
  // height for a rarely-used field — show a compact opener until it is needed.
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const byEmail = useMemo(() => new Map(staff.map((s) => [s.email, s])), [staff]);

  // Server order (designation rank, then name) is kept; the section is the designation.
  const matches = useMemo(() => {
    const q = input.trim().toLowerCase();
    return staff.filter((s) => !emails.includes(s.email) && (!q
      || s.name.toLowerCase().includes(q)
      || s.email.includes(q)
      || (s.position || '').toLowerCase().includes(q)));
  }, [staff, emails, input]);

  const pick = (person) => {
    onChange([...emails, person.email]);
    setInput('');
    setInvalid(false);
    setHighlight(0);
  };

  const commit = (text) => {
    const tokens = text.split(EMAIL_SPLIT_RE).map((s) => s.trim().toLowerCase()).filter(Boolean);
    const good = tokens.filter((t) => EMAIL_RE.test(t));
    const bad = tokens.filter((t) => !EMAIL_RE.test(t));
    const added = good.filter((t, i) => !emails.includes(t) && good.indexOf(t) === i);
    if (added.length) onChange([...emails, ...added]);
    setInput(bad.join(' '));
    setInvalid(bad.length > 0);
    return bad.length === 0;
  };

  const remove = (email) => onChange(emails.filter((e) => e !== email));

  if (emails.length === 0 && !editing) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setEditing(true)}
        title="Append fixed email addresses to every send of this event"
        className="px-2 py-1 rounded-lg border border-dashed border-slate-200 text-xs text-slate-400 hover:text-slate-600 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        + Extra Address
      </button>
    );
  }

  const showList = open && !disabled && matches.length > 0;
  const active = Math.min(highlight, matches.length - 1);

  return (
    <div className="flex-1 min-w-[240px] flex flex-wrap items-center gap-1.5">
      {emails.map((email) => {
        const person = byEmail.get(email);
        return (
          <span
            key={email}
            title={email}
            className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs border bg-slate-50 border-slate-200 text-slate-700 ${disabled ? 'opacity-50' : ''}`}
          >
            {person ? person.name : email}
            <button
              type="button"
              disabled={disabled}
              onClick={() => remove(email)}
              title={`Remove ${email}`}
              className="p-0.5 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:cursor-not-allowed"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        );
      })}
      <div className="relative flex-1 min-w-[180px]">
        <input
          value={input}
          autoFocus={editing}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            const v = e.target.value;
            setInvalid(false);
            setOpen(true);
            setHighlight(0);
            // A typed separator closes every address before it.
            if (TYPED_SPLIT_RE.test(v)) {
              const parts = v.split(TYPED_SPLIT_RE);
              const tail = parts.pop();
              commit(parts.join(' '));
              setInput((prev) => (prev ? `${prev} ${tail}` : tail));
            } else {
              setInput(v);
            }
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            // A pasted list (several addresses, any separator) goes straight in.
            if (text.split(EMAIL_SPLIT_RE).filter(Boolean).length > 1) {
              e.preventDefault();
              commit(`${input} ${text}`);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' && showList) { e.preventDefault(); setHighlight((active + 1) % matches.length); }
            if (e.key === 'ArrowUp' && showList) { e.preventDefault(); setHighlight((active - 1 + matches.length) % matches.length); }
            if (e.key === 'Enter') {
              e.preventDefault();
              // A complete address is taken as typed — "a@pkf.com" must not pick "anna@pkf.com".
              if (showList && input.trim() && !EMAIL_RE.test(input.trim())) pick(matches[active]);
              else commit(input);
            }
            if (e.key === 'Escape') { setInput(''); setInvalid(false); setOpen(false); e.target.blur(); }
          }}
          onBlur={() => { setOpen(false); if (commit(input)) setEditing(false); }}
          placeholder={emails.length ? 'Add another…' : 'Search staff or type an email'}
          title={invalid ? 'Not a valid email address' : 'Search by name or email, or type any address and press Enter'}
          className={`w-full px-2.5 py-1 border rounded-lg text-xs text-slate-700 focus:ring-2 disabled:bg-slate-50 disabled:text-slate-400 ${invalid
            ? 'border-red-300 focus:ring-red-400 focus:border-red-400'
            : 'border-slate-200 focus:ring-blue-500 focus:border-blue-500'}`}
        />
        {showList && (
          <div className="absolute z-30 mt-1 w-full min-w-[220px] max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg">
            {matches.map((s, i) => (
              <React.Fragment key={s.email}>
                {(i === 0 || matches[i - 1].position !== s.position) && (
                  <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    {s.position || 'Others'}
                  </div>
                )}
                <button
                  type="button"
                  // mousedown, not click: the input's blur would commit the typed text first.
                  onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                  onMouseEnter={() => setHighlight(i)}
                  title={s.email}
                  className={`w-full text-left px-3 py-1.5 text-xs ${i === active ? 'bg-blue-50 text-blue-700' : 'text-slate-700'}`}
                >
                  {s.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
      {invalid && <span className="text-[11px] text-red-600">Not a valid email address</span>}
    </div>
  );
};

// One event row: toggle + group chips + extra addresses. All edits PATCH
// immediately; the parent refreshes the matrix afterwards.
const EventRow = ({ flow, rule, validGroups, staff, onSaved, onError }) => {
  const [busy, setBusy] = useState(false);
  // Local copy so a second address added before the first save returns is not
  // computed from the stale list; saves go out one after another.
  const [extras, setExtras] = useState(rule.extraEmails || []);
  const saveChain = useRef(Promise.resolve());

  useEffect(() => { setExtras(rule.extraEmails || []); }, [rule.extraEmails]);

  const patch = async (body) => {
    setBusy(true);
    try {
      await fetchJSON(`/api/settings/notifications/${flow}/${rule.event}`, {
        method: 'PATCH', body: JSON.stringify(body),
      });
      await onSaved();
    } catch (e) { onError(e.message); }
    finally { setBusy(false); }
  };

  const toggleGroup = (token) => {
    const next = rule.recipients.includes(token)
      ? rule.recipients.filter((t) => t !== token)
      : [...rule.recipients, token];
    patch({ recipients: next });
  };

  const saveExtras = (list) => {
    setExtras(list);
    saveChain.current = saveChain.current.then(async () => {
      try {
        await fetchJSON(`/api/settings/notifications/${flow}/${rule.event}`, {
          method: 'PATCH', body: JSON.stringify({ extraEmails: list }),
        });
      } catch (e) { onError(e.message); }
      await onSaved();
    });
  };

  return (
    <div className={`px-4 py-2 flex flex-wrap items-center gap-x-4 gap-y-2 ${rule.enabled ? '' : 'bg-slate-50/60'}`}>
      <label className="flex items-center gap-2 w-72 shrink-0 cursor-pointer">
        <input
          type="checkbox"
          checked={rule.enabled}
          disabled={busy}
          onChange={(e) => patch({ enabled: e.target.checked })}
        />
        <span className={`text-sm ${rule.enabled ? 'text-slate-800' : 'text-slate-400'}`}>
          {EVENT_LABELS[rule.event] || rule.event}
        </span>
        {!rule.stored && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500">Default</span>
        )}
      </label>

      <div className="flex flex-wrap items-center gap-1.5">
        {validGroups.map((token) => {
          const on = rule.recipients.includes(token);
          return (
            <button
              key={token}
              type="button"
              disabled={busy || !rule.enabled}
              onClick={() => toggleGroup(token)}
              title={GROUP_HINTS[token]}
              className={`px-2 py-1 rounded-full text-xs border transition-colors ${
                on
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
              } ${(!rule.enabled || busy) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {GROUP_LABELS[token] || token}
            </button>
          );
        })}
      </div>

      <ExtraEmails
        emails={extras}
        disabled={!rule.enabled}
        onChange={saveExtras}
        staff={staff}
      />
    </div>
  );
};

// Collapsed-header summary: what is in this section without opening it.
const Pill = ({ tone = 'slate', children }) => {
  const tones = {
    slate: 'bg-slate-100 text-slate-500',
    blue: 'bg-blue-50 text-blue-700',
    amber: 'bg-amber-100 text-amber-700',
  };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
};

const SettingsNotifications = () => {
  const [masterEnabled, setMasterEnabled] = useState(true);
  const [flows, setFlows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [confirmResetSection, setConfirmResetSection] = useState(null); // SECTIONS entry
  const [openSections, setOpenSections] = useState({}); // collapsed by default

  const load = useCallback(async () => {
    try {
      const data = await fetchJSON('/api/settings/notifications');
      setMasterEnabled(data.masterEnabled);
      setFlows(data.flows || []);
      setError(null);
      setLoaded(true);
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // The account search is a convenience: without it any address can still be typed.
  useEffect(() => {
    fetchJSON('/api/settings/notifications/staff')
      .then((data) => setStaff(data.staff || []))
      .catch(() => setStaff([]));
  }, []);

  const toggleMaster = async () => {
    try {
      await fetchJSON('/api/settings/notifications/master', {
        method: 'PATCH', body: JSON.stringify({ enabled: !masterEnabled }),
      });
      await load();
    } catch (e) { setError(e.message); }
  };

  // max-w-6xl, not 5xl: an event row is label + 7 group pills + the address
  // field, and only fits on one line at this width.
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-4">
        <Link to="/manage" className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"><ArrowLeft className="w-4 h-4" />Management</Link>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <h1 className="text-xl font-semibold text-slate-900">Workflow Notifications</h1>
        <InfoTip text="Mail content is fixed — this page configures recipients only." />
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Which workflow events send email, and to whom. Changes apply immediately.
      </p>

      {error && (
        <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <span>{error}</span>
        </div>
      )}

      <div className={`mb-5 p-4 rounded-lg border flex items-center justify-between gap-3 ${masterEnabled ? 'bg-white border-slate-200' : 'bg-amber-50 border-amber-300'}`}>
        <div>
          <p className="text-sm font-medium text-slate-900">Workflow email {masterEnabled ? 'is on' : 'is muted'}</p>
          <p className="text-xs text-slate-500">
            {masterEnabled
              ? 'Events below decide what goes out.'
              : 'No workflow notification mail is sent while muted (login/OTP mail is unaffected).'}
          </p>
        </div>
        <button
          type="button"
          onClick={toggleMaster}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium ${masterEnabled
            ? 'border border-slate-300 text-slate-600 hover:bg-slate-50'
            : 'bg-amber-600 text-white hover:bg-amber-700'}`}
        >
          {masterEnabled ? 'Mute All' : 'Unmute'}
        </button>
      </div>

      {!loaded && !error && <p className="text-sm text-slate-500">Loading…</p>}

      {loaded && (
        <div className="flex items-center justify-end mb-2">
          <button
            type="button"
            onClick={() => setOpenSections(
              SECTIONS.every((s) => openSections[s.key])
                ? {}
                : Object.fromEntries(SECTIONS.map((s) => [s.key, true]))
            )}
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            {SECTIONS.every((s) => openSections[s.key]) ? 'Collapse All' : 'Expand All'}
          </button>
        </div>
      )}

      <div className={masterEnabled ? '' : 'opacity-60'}>
        {loaded && SECTIONS.map((section) => {
          const sectionFlows = section.flows
            .map((f) => flows.find((x) => x.flow === f))
            .filter(Boolean);
          if (sectionFlows.length === 0) return null;
          const open = !!openSections[section.key];
          const all = sectionFlows.flatMap((f) => f.events);
          const off = all.filter((e) => !e.enabled).length;
          const customised = all.filter((e) => e.stored).length;
          return (
            <div key={section.key} className="mb-3 bg-white rounded-lg border border-slate-200">
              <div className={`flex items-center gap-3 pr-3 ${open ? 'border-b border-slate-200 bg-slate-50/70 rounded-t-lg' : 'rounded-lg'}`}>
                <button
                  type="button"
                  onClick={() => setOpenSections((s) => ({ ...s, [section.key]: !s[section.key] }))}
                  className="flex-1 min-w-0 flex items-center gap-2 px-4 py-3 text-left"
                  aria-expanded={open}
                >
                  <ChevronRight className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`} />
                  <h2 className={sectionTitle}>{section.title}</h2>
                  <span className="text-xs text-slate-500">
                    {sectionFlows.length} flow{sectionFlows.length > 1 ? 's' : ''} · {all.length - off}/{all.length} events on
                  </span>
                  {off > 0 && <Pill tone="amber">{off} off</Pill>}
                  {customised > 0 && <Pill tone="blue">{customised} customised</Pill>}
                </button>
                {open && (
                  <button
                    type="button"
                    onClick={() => setConfirmResetSection(section)}
                    className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 text-xs text-slate-500 hover:bg-white"
                    title={`Reset every ${section.title} rule to the built-in default`}
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Reset to Defaults
                  </button>
                )}
              </div>

              {open && sectionFlows.map(({ flow, validGroups, events }) => (
                <div key={flow} className="border-t border-slate-200 first:border-t-0">
                  {/* Flow band: tinted + accent bar so the sub-block reads as a
                      block, not as a stray caption above the rows. */}
                  <div className="px-4 py-2 bg-slate-100/70 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="w-1 h-3.5 rounded-full bg-blue-400 shrink-0" />
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600">{FLOW_LABELS[flow] || flow}</h3>
                    <span className="text-[11px] text-slate-500">{events.length} events</span>
                    {FLOW_HINTS[flow] && (
                      <span className="text-[11px] text-slate-500 italic">· {FLOW_HINTS[flow]}</span>
                    )}
                  </div>
                  <div className="divide-y divide-slate-100">
                    {events.map((rule) => (
                      <EventRow
                        key={rule.event}
                        flow={flow}
                        rule={rule}
                        validGroups={validGroups}
                        staff={staff}
                        onSaved={load}
                        onError={setError}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {confirmResetSection && (
        <ConfirmDeleteModal
          title={`Reset ${confirmResetSection.title} to Defaults`}
          message={`Discard the customised rules of the ${confirmResetSection.title} section and return them to the built-in behaviour? Other sections are untouched.`}
          confirmLabel="Reset"
          onClose={() => setConfirmResetSection(null)}
          onConfirm={async () => {
            await fetchJSON('/api/settings/notifications/reset', {
              method: 'POST',
              body: JSON.stringify({ flows: confirmResetSection.flows }),
            });
            setConfirmResetSection(null);
            load();
          }}
        />
      )}
    </div>
  );
};

export default SettingsNotifications;
